<template>
    <div>
        <div class="flex-page-content flex-container">
            <div class="flex-body body-scroll">
                <div class="container my-4">

                    <h3>SSNVS &mdash; SSA Batch SSN Verification</h3>
                    <p>
                        Select clients and a date range to pull newly-added employees, generate a
                        fixed-width file to submit to SSA's <b>SSNVS</b> service (via BSO), then
                        upload SSA's result file on the batch page to flag verification problems.
                    </p>

                    <form-errors :errors="formErrors" />

                    <div class="card mb-4">
                        <div class="card-body">
                            <h5 class="card-title">New Batch</h5>

                            <div class="form-row">
                                <div class="col-12 col-md-5">
                                    <label>Clients</label>
                                    <div class="form-check">
                                        <input class="form-check-input" type="radio" id="ssnvs-mode-all" value="all" v-model="clientMode">
                                        <label class="form-check-label" for="ssnvs-mode-all">All clients</label>
                                    </div>
                                    <div class="form-check mb-1">
                                        <input class="form-check-input" type="radio" id="ssnvs-mode-select" value="select" v-model="clientMode">
                                        <label class="form-check-label" for="ssnvs-mode-select">Select client(s)</label>
                                    </div>

                                    <template v-if="clientMode === 'all'">
                                        <p class="text-muted small mb-0">
                                            All {{ clients.length }} active, SSA-enabled clients will be included.
                                        </p>
                                    </template>
                                    <template v-else>
                                        <input type="text" class="form-control form-control-sm mb-1" v-model="clientFilter" placeholder="Filter clients..." aria-label="Filter clients">
                                        <div class="border rounded p-2" style="max-height: 240px; overflow-y: auto;">
                                            <div class="form-check" v-for="c in filteredClients" :key="c.id">
                                                <input class="form-check-input" type="checkbox" :id="'ssnvs-client-' + c.id" :value="c.id" v-model="selectedClientIds">
                                                <label class="form-check-label" :for="'ssnvs-client-' + c.id">
                                                    <span class="ssnvs-client-code">{{ c.client_code }}</span> <span class="text-muted">{{ c.name }}</span>
                                                </label>
                                            </div>
                                        </div>
                                    </template>
                                </div>

                                <div class="col-12 col-md-7">
                                    <div class="form-row">
                                        <div class="col-6">
                                            <label>Date basis</label>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" id="ssnvs-basis-created" value="created_on" v-model="dateBasis">
                                                <label class="form-check-label" for="ssnvs-basis-created">Date added to GMPS</label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" id="ssnvs-basis-hired" value="date_hired" v-model="dateBasis">
                                                <label class="form-check-label" for="ssnvs-basis-hired">Date hired</label>
                                            </div>
                                        </div>
                                        <div class="col-3">
                                            <label for="ssnvs-date-start">Start</label>
                                            <input type="date" id="ssnvs-date-start" class="form-control" v-model="dateStart">
                                        </div>
                                        <div class="col-3">
                                            <label for="ssnvs-date-end">End</label>
                                            <input type="date" id="ssnvs-date-end" class="form-control" v-model="dateEnd">
                                        </div>
                                    </div>

                                    <div class="mt-3 text-right">
                                        <button class="btn btn-outline-primary" :disabled="!selectionComplete" @click.prevent="onPreview">Preview</button>
                                        <button class="btn btn-primary ml-2" :disabled="!preview || !preview.num_eligible || creating" @click.prevent="onCreate">Create Batch</button>
                                    </div>

                                    <div class="mt-3" v-if="preview">
                                        <ul class="mb-1">
                                            <li><b>{{ preview.num_eligible }}</b> employees will be submitted</li>
                                            <li><b>{{ preview.num_skipped }}</b> skipped (missing/invalid SSN or blank name)</li>
                                            <li><b>{{ preview.num_already_checked }}</b> excluded (already verified, resolved, or in an open batch)</li>
                                        </ul>
                                        <details v-if="preview.skipped.length">
                                            <summary>Skipped employees</summary>
                                            <ul>
                                                <li v-for="s in preview.skipped" :key="s.employee_id">
                                                    {{ s.client_code }}/{{ s.company_code }} &mdash; {{ s.name }} ({{ s.employee_number }}): {{ s.reason }}
                                                </li>
                                            </ul>
                                        </details>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h5>Batches</h5>
                    <table class="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Created</th>
                                <th>Clients</th>
                                <th>Range</th>
                                <th>Status</th>
                                <th class="text-right">Submitted</th>
                                <th class="text-right">Verified</th>
                                <th class="text-right">Resolved</th>
                                <th class="text-right">Problems</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="b in batches" :key="b.id" class="cursor-pointer" @click="$router.push({name: 'admin-ssnvs-batch', params: {batchId: b.id}})">
                                <td>{{ b.id }}</td>
                                <td>{{ formatDate(b.created_on) }}</td>
                                <td>{{ b.all_clients ? 'All clients' : b.clients.join(', ') }}</td>
                                <td>{{ b.date_start }} &ndash; {{ b.date_end }} ({{ b.date_basis_display }})</td>
                                <td>{{ b.status_display }}</td>
                                <td class="text-right">{{ b.num_submitted }}</td>
                                <td class="text-right">{{ b.num_verified === null ? '—' : b.num_verified }}</td>
                                <td class="text-right">{{ b.num_failed === null ? '—' : b.num_resolved }}</td>
                                <td class="text-right">{{ b.num_problems === null ? '—' : b.num_problems }}</td>
                                <td class="text-right">
                                    <button class="btn btn-sm btn-outline-primary" @click.stop="$router.push({name: 'admin-ssnvs-batch', params: {batchId: b.id}})">View</button>
                                </td>
                            </tr>
                            <tr v-if="!batches.length">
                                <td colspan="10" class="text-muted">No batches yet.</td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api'

export default {
    data() {
        return {
            clients: [],
            clientMode: 'all',
            clientFilter: '',
            selectedClientIds: [],
            dateBasis: 'created_on',
            dateStart: '',
            dateEnd: '',
            preview: null,
            creating: false,
            batches: [],
            formErrors: [],
        }
    },
    computed: {
        filteredClients() {
            const f = this.clientFilter.trim().toLowerCase()
            if (!f) {
                return this.clients
            }
            return this.clients.filter(c => (c.client_code + ' ' + (c.name || '')).toLowerCase().includes(f))
        },
        selectionComplete() {
            const clientsChosen = this.clientMode === 'all' || this.selectedClientIds.length
            return clientsChosen && this.dateStart && this.dateEnd
        },
        selectionPayload() {
            return {
                all_clients: this.clientMode === 'all',
                client_ids: this.clientMode === 'all' ? [] : this.selectedClientIds,
                date_basis: this.dateBasis,
                date_start: this.dateStart,
                date_end: this.dateEnd,
            }
        },
    },
    watch: {
        // any selection change invalidates the preview (Create is gated on it)
        selectionPayload: {
            deep: true,
            handler() {
                this.preview = null
            },
        },
    },
    methods: {
        formatDate(iso) {
            return iso ? iso.slice(0, 10) : ''
        },
        loadClients() {
            api.get('/admin/ssnvs/clients').then(resp => {
                this.clients = resp.clients
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
        loadBatches() {
            api.get('/admin/ssnvs/batches').then(resp => {
                this.batches = resp.batches
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
        onPreview() {
            this.formErrors = []
            api.post('/admin/ssnvs/preview', this.selectionPayload).then(resp => {
                this.preview = resp
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
        onCreate() {
            if (this.creating) {
                return
            }
            this.creating = true
            this.formErrors = []
            this.$store.dispatch('START_LOADING')
            api.post('/admin/ssnvs/batches', this.selectionPayload).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$router.push({name: 'admin-ssnvs-batch', params: {batchId: resp.id}})
            }).catch(errors => {
                this.creating = false
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__
            })
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'SSNVS')
        this.loadClients()
        this.loadBatches()
    },
}
</script>

<style scoped>
/* fixed-width, right-aligned codes so numbers line up and names form a column */
.ssnvs-client-code {
    display: inline-block;
    min-width: 4.5em;
    font-family: monospace;
}
</style>
